import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import restaurantService from '../services/restaurantService';
import socketService from '../services/socketService';
import { clearRemotePrintSocketContext } from '../services/remotePrintSocketHandler';
import { hydrateAuthFromElectronStore, clearSessionData } from '../utils/authStorage';
import { getElectronAppFileBase } from '../utils/publicAssets';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const navigate = useNavigate();

  // Only owners/admins go through restaurant-setup onboarding — invited
  // staff (branch managers, waiters, etc.) are assigned to an existing
  // restaurant/branch and never need it.
  const checkOnboardingStatus = async (user) => {
    if (!user || (user.role !== 'owner' && user.role !== 'admin')) {
      setNeedsOnboarding(false);
      return;
    }
    try {
      const restaurants = await restaurantService.getAllRestaurants();
      setNeedsOnboarding(!Array.isArray(restaurants) || restaurants.length === 0);
    } catch (err) {
      // Don't block the app on a failed check — assume onboarding isn't needed.
      setNeedsOnboarding(false);
    }
  };

  const refreshOnboardingStatus = async () => {
    await checkOnboardingStatus(currentUser);
  };

  useEffect(() => {
    const loadUser = async () => {
      setIsLoading(true);

      try {
        await hydrateAuthFromElectronStore();
        const storedUser = authService.getUserFromStorage();

        if (storedUser && authService.isAuthenticated()) {
          // If offline, trust the stored user and skip the network validation.
          // This keeps the cashier logged in even with no internet connection.
          if (!navigator.onLine) {
            console.log('[Auth] Offline — using stored user, skipping API validation');
            setCurrentUser(storedUser);
            setIsLoading(false);
            return;
          }
          const user = await authService.getCurrentUser();
          setCurrentUser(user);
          await checkOnboardingStatus(user);
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        // Keep the stored user whenever the failure was NOT an explicit token
        // rejection. getCurrentUser only clears storage on 401/403, so if the
        // token is still there the error was a network/server problem (offline,
        // server cold start, timeout) — do not force a re-login.
        const storedUser = authService.getUserFromStorage();
        if (storedUser && authService.isAuthenticated()) {
          console.log('[Auth] Could not validate session (network/server error) — keeping stored user');
          setCurrentUser(storedUser);
          setIsLoading(false);
          return;
        }
        console.error("Error loading user:", err);
        setCurrentUser(null);
        await authService.logout();
      }

      setIsLoading(false);
    };

    loadUser();
  }, []);

  const register = async (userData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await authService.register(userData);

      // Owner signups must verify their email first — no token is returned yet.
      if (data.requiresVerification) {
        setIsLoading(false);
        navigate('/verify-email', { state: { email: data.email } });
        return data;
      }

      setCurrentUser(data.user);
      setIsLoading(false);

      navigate('/dashboard');
      return data;
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'Registration failed');
      throw err;
    }
  };

  // Verify the emailed signup code; on success the user is logged in.
  const verifyEmailCode = async ({ email, code }) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await authService.verifyEmailCode({ email, code });
      if (data.user && !data.user.permissions) {
        data.user.permissions = getDefaultPermissions(data.user.role);
      }
      setCurrentUser(data.user);
      setIsLoading(false);
      setNeedsOnboarding(true);
      // Verification is owner-only and they have no restaurant yet — onboard them.
      navigate('/onboarding');
      return data;
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'Verification failed');
      throw err;
    }
  };

  const resendCode = async (email) => {
    setError(null);
    try {
      return await authService.resendCode(email);
    } catch (err) {
      setError(err.message || 'Could not resend code');
      throw err;
    }
  };

  const login = async (credentials) => {
    setIsLoading(true);
    setError(null);

    try {
      // Clear any stale POS session data from a previous user's session.
      // This handles the case where the previous user closed the tab without
      // logging out — their selectedBranch / selectedRestaurant / branch cache
      // would otherwise be inherited by the new user on first render.
      clearSessionData();

      const data = await authService.login(credentials);
      
      if (data.user) {
        if (!data.user.permissions) {
          data.user.permissions = getDefaultPermissions(data.user.role);
        }
        
        if (!data.user.branchPermissions) {
          data.user.branchPermissions = {
            menu: [],
            tables: []
          };
        } else {
          if (!data.user.branchPermissions.menu) {
            data.user.branchPermissions.menu = [];
          }
          if (!data.user.branchPermissions.tables) {
            data.user.branchPermissions.tables = [];
          }
        }
      }
      
      setCurrentUser(data.user);
      // Store pending alerts in state so OwnerAlertModal can show them
      if (data.alerts?.length > 0) {
        setPendingAlerts(data.alerts);
      }
      setIsLoading(false);

      let onboardingPending = false;
      if (data.user.role === 'owner' || data.user.role === 'admin') {
        try {
          const restaurants = await restaurantService.getAllRestaurants();
          onboardingPending = !Array.isArray(restaurants) || restaurants.length === 0;
          setNeedsOnboarding(onboardingPending);
        } catch (_) {
          /* non-blocking */
        }
      } else {
        setNeedsOnboarding(false);
      }

      if (onboardingPending && !window.electronAPI?.isElectron) {
        navigate('/onboarding');
        return data;
      }

      const singleBranchId = data.user.branchIds?.[0] || data.user.branchId;

      // ── Desktop app: managers land on hub dashboard; floor staff go to POS ──
      if (window.electronAPI?.isElectron) {
        const hubRoles = ['branch_manager', 'manager', 'restaurant_manager', 'owner', 'admin', 'branch_admin'];
        if (hubRoles.includes(data.user.role)) {
          navigate('/dashboard');
        } else {
          navigate(singleBranchId ? `/pos/${singleBranchId}` : '/pos');
        }
        return data;
      }

      // ── Web app: role-based routing ────────────────────────────────────────
      // Floor staff go to the POS; platform admins to the admin panel.
      // Every management role lands on /dashboard (DashboardRouter picks the
      // role-appropriate dashboard variant).
      if (data.user.role === 'chef' || data.user.role === 'waiter') {
        navigate(singleBranchId ? `/pos/${singleBranchId}` : '/waiter/dashboard');
      } else if (data.user.role === 'platform_admin') {
        navigate('/admin');
      } else if (
        data.user.role === 'owner' ||
        data.user.role === 'admin' ||
        data.user.role === 'branch_admin' ||
        data.user.role === 'branch_manager' ||
        data.user.permissions?.manageRestaurants ||
        data.user.permissions?.manageBranches
      ) {
        navigate('/dashboard');
      } else if (data.user.branchPermissions) {
        const hasBranchMenuAccess = data.user.branchPermissions.menu &&
          data.user.branchPermissions.menu.length > 0;
        const hasBranchTableAccess = data.user.branchPermissions.tables &&
          data.user.branchPermissions.tables.length > 0;
        if (hasBranchMenuAccess) {
          navigate('/assigned-menus');
        } else if (hasBranchTableAccess) {
          navigate('/assigned-tables');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
      
      return data;
    } catch (err) {
      setIsLoading(false);
      // Unverified owner trying to log in → bounce them to the code screen.
      if (err && err.code === 'EMAIL_NOT_VERIFIED') {
        navigate('/verify-email', { state: { email: err.email } });
        return;
      }
      setError(err.message || 'Login failed');
      throw err;
    }
  };

  const getDefaultPermissions = (role) => {
    switch (role) {
      case 'owner':
      case 'admin':
        return {
          manageUsers: true,
          manageMenu: true,
          manageTables: true,
          accessPOS: true,
          manageRestaurants: true,
          manageBranches: true
        };
      case 'branch_admin':
        return {
          manageUsers: true,
          manageMenu: true,
          manageTables: true,
          accessPOS: true,
          manageRestaurants: false,
          manageBranches: true
        };
      case 'restaurant_manager':
      case 'manager':
        return {
          manageUsers: false,
          manageMenu: true,
          manageTables: true,
          accessPOS: true,
          manageRestaurants: false,
          manageBranches: true
        };
      case 'branch_manager':
        return {
          manageUsers: false,
          manageMenu: true,
          manageTables: true,
          accessPOS: true,
          manageRestaurants: false,
          manageBranches: false
        };
      case 'waiter':
        return {
          manageUsers: false,
          manageMenu: false,
          manageTables: false,
          accessPOS: true,
          manageRestaurants: false,
          manageBranches: false
        };
      case 'chef':
        return {
          manageUsers: false,
          manageMenu: false,
          manageTables: false,
          accessPOS: true,
          manageRestaurants: false,
          manageBranches: false
        };
      default:
        return {
          manageUsers: false,
          manageMenu: false,
          manageTables: false,
          accessPOS: false,
          manageRestaurants: false,
          manageBranches: false
        };
    }
  };

  const logout = async () => {
    await authService.logout();

    try {
      socketService.disconnect();
      clearRemotePrintSocketContext();
    } catch (_) {
      /* non-blocking */
    }

    if (window.electronAPI?.isElectron) {
      // FULL renderer reboot into the sign-in page. Two reasons:
      // 1. A hash-only location.replace does NOT reload — the signed-in POS
      //    tree re-renders with the user already gone for a tick, and any
      //    throw in that teardown window blanked the screen (Biryani House
      //    sign-out report, 2026-08-02). Rebooting skips that window whole.
      // 2. It guarantees zero Redux/context state leaks between staff users.
      // setCurrentUser(null) is deliberately NOT called here — the reload
      // rebuilds React from scratch with the auth storage already cleared.
      const base = getElectronAppFileBase();
      window.location.replace(`${base}#/signin`);
      window.location.reload();
      return;
    }

    setCurrentUser(null);
    navigate('/signin', { replace: true });
  };

  const forgotPassword = async (email) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await authService.forgotPassword(email);
      setIsLoading(false);
      return data;
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'Failed to process request');
      throw err;
    }
  };

  const resetPassword = async (token, password) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await authService.resetPassword(token, password);
      setIsLoading(false);
      return data;
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'Failed to reset password');
      throw err;
    }
  };

  const value = {
    currentUser,
    isLoading,
    error,
    pendingAlerts,
    setPendingAlerts,
    needsOnboarding,
    refreshOnboardingStatus,
    register,
    login,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmailCode,
    resendCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthProvider;